import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { APIProvider } from "@vis.gl/react-google-maps";
import HomePage from "@/pages/HomePage";
import BookingConfirmation from "@/pages/BookingConfirmation";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import FAQPage from "@/pages/FAQPage";
import DashcamFAQPage from "@/pages/DashcamFAQPage";
import ContactPage from "@/pages/ContactPage";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminBookingDetail from "@/pages/admin/AdminBookingDetail";
import AdminCalendar from "@/pages/admin/AdminCalendar";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminWaitlist from "@/pages/admin/AdminWaitlist";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminReports from "@/pages/admin/AdminReports";
import AdminManualBooking from "@/pages/admin/AdminManualBooking";
import CustomerPortal from "@/pages/CustomerPortal";
import NotFound from "@/pages/NotFound";
import { ADMIN_BASE, ADMIN_LOGIN_URL } from "@/constants/adminRoutes";

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-sarathi-background flex items-center justify-center text-sarathi-text-muted">Loading...</div>;
  if (!user) return <Navigate to={ADMIN_LOGIN_URL} replace />;
  return children;
}

function AdminLoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-sarathi-background flex items-center justify-center text-sarathi-text-muted">Loading...</div>;
  if (user) return <Navigate to={ADMIN_BASE} replace />;
  return <AdminLogin />;
}

function App() {
  return (
    <div className="App min-h-screen bg-sarathi-background">
      <Toaster position="top-right" richColors />
      <APIProvider apiKey={GOOGLE_MAPS_KEY} libraries={["places"]}>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              {/* Public */}
              <Route path="/" element={<HomePage />} />
              <Route path="/booking/:bookingId" element={<BookingConfirmation />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/dashcam-faqs" element={<DashcamFAQPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/my-trips" element={<CustomerPortal />} />
              <Route path="/account" element={<CustomerPortal />} />

              {/* Admin Auth — path INTENTIONALLY obscure. Defense-in-depth
                  only — real gate is Cloudflare Zero Trust + TOTP. */}
              <Route path={`${ADMIN_BASE}/login`} element={<AdminLoginRoute />} />

              {/* Protected Admin */}
              <Route path={ADMIN_BASE} element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route index element={<AdminDashboard />} />
                <Route path="bookings" element={<AdminBookings />} />
                <Route path="bookings/new" element={<AdminManualBooking />} />
                <Route path="bookings/:id" element={<AdminBookingDetail />} />
                <Route path="calendar" element={<AdminCalendar />} />
                <Route path="waitlist" element={<AdminWaitlist />} />
                <Route path="payments" element={<AdminPayments />} />
                <Route path="reports" element={<AdminReports />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>

              {/* Catch-all 404 — explicit, never confirms a guessed path.
                  Bare `/caldera`, `/admin`, `/admin/login`, etc. all hit here. */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </APIProvider>
    </div>
  );
}

export default App;
